import {ApolloLink, Observable} from '@apollo/client';

import type {InternalOperation} from 'src/types';

const callbacks = new Set<(ids: string[]) => void>();

export function registerComponentKillCallback(
  callback: (ids: string[]) => void,
): () => void {
  callbacks.add(callback);
  return () => {
    callbacks.delete(callback);
  };
}

export default function createComponentKillswitchLink(): ApolloLink {
  return new ApolloLink(function componentKillswitchLink(
    operation: InternalOperation,
    forward,
  ) {
    return new Observable(function componentKillswitchObserver(observer) {
      const subscription = forward(operation).subscribe({
        next: function componentKillswitchLinkNext(result) {
          const context = operation.getContext();
          const responses =
            context.responses ?? (context.response && [context.response]) ?? [];

          responses.forEach((response) => {
            const killswitchHeader = response?.headers?.get?.(
              'X-Component-Killswitch',
            );

            if (killswitchHeader) {
              const componentIds = killswitchHeader
                .split(',')
                .map((id) => id.trim())
                .filter((id) => id.length > 0);

              if (componentIds.length > 0) {
                callbacks.forEach((callback) => {
                  callback(componentIds);
                });
              }
            }
          });

          return observer.next(result);
        },

        error: function componentKillswitchLinkError(error) {
          return observer.error(error);
        },

        complete: function componentKillswitchLinkComplete() {
          return observer.complete();
        },
      });

      return function componentKillswitchLinkUnsubscribe(): void {
        if (subscription) {
          subscription.unsubscribe();
        }
      };
    });
  });
}
